import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { MessageCircle, CheckCircle2, ShieldCheck, Sparkles, ExternalLink, Stethoscope } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const WA_BUSINESS_NUMBER =
  process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER || '27839726081';

const BOT_FEATURES = [
  { icon: '🌅', label: 'today', desc: "Ask 'today' — get your full schedule with names + times" },
  { icon: '✏️', label: 'add daysheet', desc: "Add a session by chatting — I'll ask the follow-ups" },
  { icon: '🧾', label: 'invoices', desc: "'Sarah's invoice for May' → I generate the PDF + send it back" },
  { icon: '💛', label: 'support', desc: 'Type *support* and I connect you straight to Michaela' },
  { icon: '✨', label: 'ideas', desc: 'Activity ideas, GLP tips, parent phrasing — just ask' },
];

export default function WhatsAppActivationCard() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [linkedPhone, setLinkedPhone] = useState('');
  const [phone, setPhone] = useState('');
  const [popiaAck, setPopiaAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [pingLoading, setPingLoading] = useState(false);

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setEnabled(!!res.data.enabled);
      setLinkedPhone(res.data.phone || '');
      if (res.data.phone) setPhone(res.data.phone);
    } catch (e) {
      // 403 if not therapist — silently ignore
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!phone.trim()) {
      toast.error('Please enter your WhatsApp number.');
      return;
    }
    // Strip + spaces dashes for length check
    const digits = phone.replace(/[\s\-+()]/g, '');
    if (digits.length < 10) {
      toast.error(
        'That number looks too short. Use full international format including country code (e.g. 27795157938 — no +).'
      );
      return;
    }
    if (!popiaAck && !editing) {
      toast.error('Please acknowledge the POPIA notice to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/whatsapp/activate`,
        { phone: phone.trim(), popia_acknowledged: true },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setEnabled(true);
      setLinkedPhone(res.data.whatsapp_phone);
      setEditing(false);
      toast.success(
        editing
          ? 'WhatsApp number updated.'
          : 'WhatsApp bot activated. 💛'
      );
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        'Could not activate. Please check the number and try again.';
      toast.error(typeof msg === 'string' ? msg : 'Activation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/whatsapp/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setEnabled(false);
      toast.success('WhatsApp bot disabled.');
    } catch (e) {
      toast.error('Could not disable bot.');
    } finally {
      setSubmitting(false);
    }
  };

  const runDiagnostic = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const r = await axios.get(`${API}/whatsapp/diagnostic`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setDiag({ kind: 'diagnostic', data: r.data });
    } catch (e) {
      setDiag({
        kind: 'error',
        data: e?.response?.data || { error: 'Diagnostic call failed', detail: String(e) },
      });
    } finally {
      setDiagLoading(false);
    }
  };

  const runPingMe = async () => {
    setPingLoading(true);
    setDiag(null);
    try {
      const r = await axios.post(
        `${API}/whatsapp/diagnostic/ping-me`,
        {},
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setDiag({ kind: 'ping', data: r.data });
      toast.success('Ping sent — check your WhatsApp 💛');
    } catch (e) {
      setDiag({
        kind: 'ping-error',
        data: e?.response?.data || { error: 'Ping failed', detail: String(e) },
      });
    } finally {
      setPingLoading(false);
    }
  };

  if (loading) return null;

  return (
    <Card
      className="p-6 bg-white rounded-3xl border border-stone-100 mb-8"
      data-testid="whatsapp-activation-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-primary-900">
            WhatsApp Assistant
          </h3>
          <p className="text-sm text-stone-500">
            Manage your day from WhatsApp — schedule, invoices, quick Qs.
          </p>
        </div>
      </div>

      {enabled && !editing ? (
        <div
          className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-start gap-3"
          data-testid="whatsapp-active-state"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-emerald-900">Bot active</p>
            <p className="text-sm text-emerald-800">
              Linked number:{' '}
              <span
                className="font-mono"
                data-testid="whatsapp-linked-phone"
              >
                +{linkedPhone}
              </span>
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              💡 Make sure this is your full international number (e.g. <span className="font-mono">27795157938</span> for SA).
              If the country code is missing, the bot won't recognize incoming messages.
            </p>
            <p className="text-sm text-emerald-700 mt-2">
              Send <span className="font-mono">help</span> on WhatsApp to see
              what I can do.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={`https://wa.me/${WA_BUSINESS_NUMBER}?text=help`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-try-it-btn"
              >
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  Open WhatsApp
                </Button>
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditing(true);
                  setPhone(linkedPhone);
                }}
                data-testid="whatsapp-edit-btn"
              >
                Edit number
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeactivate}
                disabled={submitting}
                data-testid="whatsapp-deactivate-btn"
              >
                Disable bot
              </Button>
            </div>

            {/* Super-admin diagnostic */}
            <div className="mt-4 pt-4 border-t border-emerald-200">
              <p className="text-xs font-semibold text-emerald-900 mb-2 flex items-center gap-1.5">
                <Stethoscope className="w-3.5 h-3.5" /> Diagnostics (super-admin)
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runDiagnostic}
                  disabled={diagLoading}
                  data-testid="whatsapp-diagnostic-btn"
                >
                  {diagLoading ? 'Running…' : 'Run diagnostic'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runPingMe}
                  disabled={pingLoading}
                  data-testid="whatsapp-ping-me-btn"
                >
                  {pingLoading ? 'Pinging…' : 'Send test ping to my number'}
                </Button>
              </div>
              {diag && (
                <pre
                  className="text-[11px] bg-white rounded-xl p-3 border border-stone-200 overflow-auto max-h-72 font-mono leading-snug text-stone-800"
                  data-testid="whatsapp-diagnostic-output"
                >
                  {JSON.stringify(diag.data, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="whatsapp-activate-form">
          {editing && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
              Updating your linked WhatsApp number. The new number will replace{' '}
              <span className="font-mono">+{linkedPhone}</span>.
            </div>
          )}
          {/* Enticing feature description */}
          {!editing && (
          <div
            className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200"
            data-testid="whatsapp-feature-pitch"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <p className="text-sm font-semibold text-emerald-900">
                Run your day from WhatsApp
              </p>
            </div>
            <p className="text-sm text-emerald-800 mb-3">
              The PlayFirst Assistant lives where you already are. No app to
              open between sessions — just text it like a colleague.
            </p>
            <ul className="space-y-1.5">
              {BOT_FEATURES.map((f) => (
                <li
                  key={f.label}
                  className="text-sm text-emerald-900 flex items-start gap-2"
                >
                  <span aria-hidden="true">{f.icon}</span>
                  <span>
                    <span className="font-mono font-medium">{f.label}</span>{' '}
                    — <span className="text-emerald-800">{f.desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          )}

          <div>
            <Label htmlFor="wa-phone" className="text-sm">
              Your WhatsApp number
            </Label>
            <Input
              id="wa-phone"
              data-testid="whatsapp-phone-input"
              placeholder="e.g. 27791234567 (international, no +)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-stone-500 mt-1">
              International format, digits only. We use this to recognize you
              when you message the PlayFirst bot.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-stone-50 border border-stone-200">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-stone-600 mt-0.5 shrink-0" />
              <div className="text-sm text-stone-700 space-y-2">
                <p className="font-medium">Privacy notice (POPIA aligned)</p>
                <ul className="list-disc list-inside space-y-1 text-stone-600">
                  <li>
                    Messages are processed by Meta's WhatsApp Cloud API and
                    PlayFirst.
                  </li>
                  <li>
                    Inbound text is kept only while a multi-turn flow is
                    active (auto-clears after 30 min).
                  </li>
                  <li>
                    Command logs (no message text) retained 90 days for
                    audit.
                  </li>
                  <li>
                    You can disable the bot any time on this page.
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-3 flex items-start gap-2">
              <Checkbox
                id="wa-popia"
                data-testid="whatsapp-popia-checkbox"
                checked={popiaAck}
                onCheckedChange={(v) => setPopiaAck(!!v)}
              />
              <Label
                htmlFor="wa-popia"
                className="text-sm leading-snug cursor-pointer"
              >
                I acknowledge the privacy notice and consent to processing
                WhatsApp messages on this number.
              </Label>
            </div>
          </div>

          <Button
            onClick={handleActivate}
            disabled={submitting}
            className="w-full sm:w-auto"
            data-testid="whatsapp-activate-btn"
          >
            {submitting ? 'Activating…' : 'Activate WhatsApp bot'}
          </Button>
        </div>
      )}
    </Card>
  );
}
